import { getAuthorizationHeader, getAuthParamsFromUrl } from './auth'
import { getApiBaseUrl, type Environment } from './config'

export type RequestConfig = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  headers?: HeadersInit
  env?: Environment
  authToken?: string
  authType?: 'Bearer' | 'Basic'
}

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * API client with automatic authentication from URL params.
 */
export const api = async <T>(
  endpoint: string,
  config: RequestConfig = {},
): Promise<T> => {
  const {
    method = 'GET',
    body,
    headers,
    env = 'tst',
    authToken,
    authType = 'Bearer',
  } = config
  const baseUrl = getApiBaseUrl(env)

  // Get auth header
  let authHeader: string | undefined
  if (authToken) {
    authHeader = `${authType} ${authToken}`
  } else {
    // Fallback: Email/code from URL params uses Basic auth
    const { email, code } = getAuthParamsFromUrl()
    if (email && code) {
      authHeader = getAuthorizationHeader(email, code)
    }
  }

  const response = await fetch(`${baseUrl}${endpoint}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(authHeader && { Authorization: authHeader }),
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
    mode: 'cors',
    credentials: 'omit',
  })

  if (!response.ok) {
    const message = await response.text()
    throw new ApiError(response.status, message || 'An error occurred')
  }

  return response.json()
}
